import Component from '~/components/component';

export const SelectEvent = {
    SELECT_CHANGE: '__se_select_change'
};

export const SelectClasses = {
    ITEM_ACTIVE: 'active',
    OPEN: 'open',
};


/**
 * Custom select element and option list
 *
 * JS example :
 * this_select = new Select('.js-select)
 * this._select._getValues();
 *
 * HTML example :
 *
 *   <select name="myselect"
 *           class="form-select form-select-hidden js-form-select"
 *           data-selected="placeholder"
 *           data-icon="svg-arrow-down">
 *       <option value="0" class="placeholder">placeholder</option>
 *       <option value="1">1</option>
 *       <option value="2">2</option>
 *   </select>
 */


export default class Select extends Component {
    constructor(...args) {
        super(...args);

        bindAll( this, [
            '_handleListItemClick',
            '_handleSelectStyledClick',
            '_handleBodyClick'
        ]);

        this.placeholderText = '';
        this._selectValue = null;
        this._open = false;

        this._retrieveDom();
        this._createDom();
    }

    /**
     * Set select Node element value
     * @param name - Select option name
     * @param value - Select option value
     */
    set selectValue({name, value}) {
        // set active item
        this.setActiveItem({name, value});
        // assign value
        this._selectValue = {name, value};
        // emit event
        const event = new CustomEvent("onChange", { detail: { name, value } });
        this.dom.component.dispatchEvent(event);
    }

    /**
     * get Select Value and name
     * @return {{name: string, value: string}}
     */
    get selectValue() {
        return this._selectValue;
    }

    /**
     * Get select icon from data-icon on select node or default icon 'svg-arrow-down'
     * @return {string}
     */
    get selectIcon() {
        return this.dom.select.dataset.icon || 'svg-arrow-down';
    }

    init() {
        this._initEvents();
        this._setPlaceholder();

        /** Try to force initial value (selected default value) **/
        var selected = this.dom.component.querySelector( '.js-form-select-item.active:not(.form-select-item-placeholder)' );
        if( selected ) {
            this._changeSelectValue( '', selected );
        }
    }

    /**
     * Retrieve select dom element
     * @private
     */
    _retrieveDom() {
        this.dom.select = this.dom.component.querySelector('.js-form-select');
        this.dom.options = this.dom.component.querySelectorAll('.js-form-select > option');
    }

    /**
     * Create custom select ui dom
     * @private
     */
    _createDom() {
        const fragment = document.createDocumentFragment();

        // Create select styled div
        this.dom.selectStyled = createHTMLElement('div',  { className: 'form-select-styled js-form-select-styled' });
        fragment.appendChild(this.dom.selectStyled);

        // Create select styled text span
        this.dom.selectStyledText = createHTMLElement('span', {
                className: 'js-form-select-styled-text'
            });
        this.dom.selectStyled.appendChild(this.dom.selectStyledText);

        const svgIcon = this._createSelectDomIcon();
        this.dom.selectStyled.appendChild(svgIcon);

        // Create list ul
        this.dom.optionList = createHTMLElement('ul', { className: 'form-select-options js-form-select-options' });
        fragment.appendChild(this.dom.optionList);

        // Form select option li from select>option
        this._createOptionList();

        this.dom.component.appendChild(fragment);
    }

    /**
     * Create the svg dom element for custom select icon
     * @return {SVGElement}
     * @private
     */
    _createSelectDomIcon() {
        var svgElem = document.createElementNS('http://www.w3.org/2000/svg', 'svg'),
            useElem = document.createElementNS('http://www.w3.org/2000/svg', 'use');

        // set svg element classes
        svgElem.setAttribute('class','icon svg-arrow-down');

        // Set icon ref
        useElem.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', '#' + this.selectIcon);

        svgElem.appendChild(useElem);
        return svgElem;
    }

    /**
     * Create a list item from select > option
     * @param option
     */
    _createOptionListItem(option) {
        let className = 'form-select-item js-form-select-item';

        // set placeholder
        if(option.classList.contains('placeholder')) {
            className += ' form-select-item-placeholder';
            this.placeholderText = option.textContent;
        }
        // set active option
        if(option.defaultSelected) {
            className += ' active';
            this.placeholderText = option.textContent;
        }

        // Set attributes
        const itemAttributes = {};
        for (var i = 0; i < option.attributes.length; i++) {
            // Store reference to current attr
            const attr = option.attributes[i];
            // If attribute nodeName different than class and value
            if (attr.nodeName !== 'value' && attr.nodeName !== 'class' && attr.nodeName !== 'selected') {
                itemAttributes[attr.nodeName] = attr.nodeValue;
            }
        }

        return createHTMLElement('li', {
            className: className,
            text: option.textContent,
            attributes: itemAttributes
        });
    }

    /**
     * Create custom option list li from select
     * @private
     */
    _createOptionList() {
        this.dom.optionListItems = [];
        this.dom.options.forEach(option => {

            const optionListItem = this._createOptionListItem(option);

            // Set placeholder
            if(option.classList.contains('placeholder')) {
                this.dom.optionListPlaceholder = optionListItem;
            }

            // add to list items array
            this.dom.optionListItems.push(optionListItem);

            // add to dom
            this.dom.optionList.appendChild(optionListItem);

        });
    }


    /**
     * Set active item in select UI, select element and dispatch necessary events
     * @param name - Select option name
     * @param value - Select option value
     */
    setActiveItem({name, value}) {

        // get current item
        const targetListItem = [].find.call(this.dom.optionListItems, el => el.dataset.value === value);

        // get current item slug
        const currentSlug = targetListItem ? targetListItem.dataset.slug : undefined;

        // remove already active item
        [].forEach.call(this.dom.optionListItems, el => el.classList.remove(SelectClasses.ITEM_ACTIVE));

        // remove active to list
        this.dom.selectStyled.classList.remove(SelectClasses.OPEN);

        // assign value name to styled select text
        this.dom.selectStyledText.textContent = name;

        // set current list item active
        if(targetListItem) {
            targetListItem.classList.add(SelectClasses.ITEM_ACTIVE);
            // add name to select data selected
            this.dom.select.dataset.selected = name;
        }

        // assign value to select
        this.dom.select.value = value;

        // dispatch dom element change event
        const changeEvent = new CustomEvent("change", {
            detail: {
                element: this.dom.select
            }
        });
        this.dom.select.dispatchEvent(changeEvent);

        // emit component change event
        this.emit(SelectEvent.SELECT_CHANGE, {currentTarget:this.dom.select, slug: currentSlug});
    }

    /**
     * Init component dom elements events
     * @private
     */
    _initEvents() {
        // Listen click on each option list item
        [].forEach.call(this.dom.optionListItems, (el) => {
            el.addEventListener('click', this._handleListItemClick);
        });

        // Listen click on select styled
        this.dom.selectStyled.addEventListener('click', this._handleSelectStyledClick);

        // listen click on body
        document.body.addEventListener('click', this._handleBodyClick);
    }

    /**
     * Handle click event on option list item
     * @param {Event} e
     * @private
     */
    _handleListItemClick(e) {
        e.stopPropagation();
        this._changeSelectValue(e);
    }

    /**
     * Handle click event on select styled node
     * @param {Event} e
     * @private
     */
    _handleSelectStyledClick(e) {
        e.stopPropagation();
        this._closeOthers();
        this._toggleList();
    }

    /**
     * Handle click on body
     * @private
     */
    _handleBodyClick() {
        this.closeSelect();
    }

    /**
     * Get placeholder
     * @private
     */
    _setPlaceholder() {
        this.dom.selectStyledText.textContent = this.placeholderText;
    }

    /**
     * Assign select value on list item
     * @private
     */
    _changeSelectValue(e, el = null) {
        const targetListItem = ( el == null ) ? e.currentTarget : el;
        this.selectValue = {
            name: targetListItem.textContent,
            value: targetListItem.getAttribute('data-value')
        };
    }

    /**
     * Close other open select lists on component
     * @private
     */
    _closeOthers() {
        const otherSelects = document.querySelectorAll('.form-select-styled.open');
        [].forEach.call(otherSelects, (el, i, els) => {
            if(el !== this.dom.selectStyled) {
                el.classList.remove(SelectClasses.OPEN);
            }
        });
    }

    /**
     * Toggle list open/close
     * @private
     */
    _toggleList() {
        this.dom.selectStyled.classList.toggle(SelectClasses.OPEN);
    }

    /**
     * Close select list
     */
    closeSelect() {
        this.dom.selectStyled.classList.remove(SelectClasses.OPEN);
    }

    /**
     * Reset select
     */
    reset() {
        this.selectValue = {name: this.placeholderText, value: null};
    }

}



// WEBPACK FOOTER //
// src/js/components/select/select.js